import './styles/ReviewTab.scss';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import ReviewControls from './ReviewControls';
import history from '../../../shared_history';
import queryString from 'query-string';
import useReviewContact from './hooks/useReviewContact';
import useReviewPaginator from './hooks/useReviewPaginator';
import { compose } from 'redux';
import { contacts, reviews } from '../../../ducks';
import { duckify, singleDuck } from 'utils/duckify';
import { withRouter } from 'react-router';

/**
 * Re-packages review contact and pagination info for subcomponents.
 *
 * @param reviewContact the review contact from the review contacts controller
 * @param reviewPaginator the pagination object from useReviewPaginator
 */
const buildContext = ({ reviewContact, reviewPaginator: paginator }) => {
  const { contact, fields, ready } = reviewContact;
  const documents = contact._documents || [];
  const reviews = contact._reviews || [];
  return {
    contact,
    documents,
    fields,
    paginator,
    ready,
    reviews,
  };
};

/**
 * Top-level component for the review tab on the contact record.
 *
 * Child components are the individual pages within the review workflow like the cover sheet, document review pages,
 * activities page, reader summary, etc. These are selected based on the route definitions. This component packages up
 * data related to the review and passes them to child components as props. These props include:
 *
 *  * `review` - the Review object
 *  * `reviewMeta` - duck metadata for the Review object
 *  * `reviewContext` - data from the Review Contact endpoint and pagination data
 *  * `schoolYearId` - the review year
 *
 * The routes for this component generally have a reviewId in them but this component also facilities redirecting to
 * an appropriate review if the reviewId is not known. These routes illustrate this behavior:
 *
 *  1. `/contacts/:contactId/reviews` - redirect user to assigned review in the contact's active school year
 *  2. `/contacts/:contactId/reviews?schoolYearId=:schoolYearId` - redirect user to assigned review in the school
 *        year specified by the query parameter
 *  3. `/contacts/:contactId/reviews/:reviewId` - no redirect, user is looking at a specific review
 *
 * @param children the child components
 * @param contact the Contact object
 * @param duckMeta loading state for ducks provided by duckify
 * @param location location object provided by withRouter
 * @param params URL parameters provided by react-router
 * @param review the Review object
 * @return {JSX.Element}
 */
const ReviewTab = ({ children, contact, duckMeta, location, params, review }) => {
  const { contactId, reviewId } = params;
  const search = queryString.parse(location.search);
  // school year priority: review, query param, contact
  const schoolYearId = reviewId
    ? review && review.school_year_id
    : (search.schoolYearId || contact && contact.school_year_id);
  const reviewContact = useReviewContact({ contactId, schoolYearId, redirect: !reviewId });
  const reviewPaginator = useReviewPaginator({ path: location.pathname, review, reviewContact });
  const reviewContext = buildContext({ reviewContact, reviewPaginator });
  const reviewMeta = duckMeta ? duckMeta.review : {};
  const cloneChildren = (props) => React.Children.map(children, child => React.cloneElement(child, props));

  // performs "redirect" as described in jsdoc above
  useEffect(() => {
    const { ready, reviews } = reviewContext;
    if (!reviewId && ready && reviews.length !== 0) {
      history.replace(`/contacts/${contactId}/reviews/${reviews[0].id}`);
    }
  }, [reviewContact, reviewId])

  return (
    <div className="reviewing">
      <FlashRoot/>

      {!reviewId && cloneChildren({ reviewContext, schoolYearId })}

      {reviewId && <React.Fragment>
        {cloneChildren({ review, reviewMeta, reviewContext, schoolYearId })}

        {reviewContext.ready && reviewMeta.ready &&
          <ReviewControls review={review} reviewContext={reviewContext}/>}
      </React.Fragment>}
    </div>
  );
};

ReviewTab.propTypes = {
  /** Params from react router */
  params: PropTypes.shape({
    contactId: PropTypes.string.isRequired,
    reviewId: PropTypes.string,
  }),
};

const mapDucksToProps = ({ params }) => {
  const { contactId, reviewId } = params;
  const review = reviewId ? { review: singleDuck(reviews, reviewId) } : {};

  return {
    contact: singleDuck(contacts, contactId),
    ...review
  };
};

export default compose(
  withRouter,
  duckify(mapDucksToProps)
)(ReviewTab);
